import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import {
  Store,
  Globe,
  Palette,
  Eye,
  Link,
  Plus,
  Settings,
  Package,
  Users,
  TrendingUp,
} from "lucide-react";
import StoreManagement from "./StoreManagement";
import WebsiteCustomizer from "./WebsiteCustomizer";
import StorePreview from "./StorePreview";
import StoreLinkGenerator from "./StoreLinkGenerator";

interface StoreBuilderProps {
  onClose?: () => void;
}

const StoreBuilder = ({ onClose }: StoreBuilderProps) => {
  const [activeTab, setActiveTab] = useState("overview");
  const [storeData, setStoreData] = useState({
    name: "My Kente Store",
    description: "Authentic Kente designs crafted with love",
    logo: "",
    primaryColor: "#8B4513",
    secondaryColor: "#DAA520",
    accentColor: "#FFD700",
    items: [],
    isPublished: false,
    storeUrl: "",
    contactInfo: {
      email: "info@kentestore.com",
      phone: "+233 24 123 4567",
      location: "Accra, Ghana",
      businessHours: {
        weekdays: "9:00 AM - 6:00 PM",
        saturday: "10:00 AM - 4:00 PM",
        sunday: "Closed",
      },
    },
  });

  const handleStoreUpdate = (updates: Partial<typeof storeData>) => {
    setStoreData((prev) => ({ ...prev, ...updates }));
  };

  const storeStats = {
    totalItems: storeData.items.length,
    publishedItems: storeData.items.filter((item: any) => item.isPublished)
      .length,
    totalViews: 0,
    totalSales: 0,
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary/10 rounded-lg">
            <Store className="h-6 w-6 text-primary" />
          </div>
          <div>
            <h2 className="text-2xl font-bold">Store Builder</h2>
            <p className="text-muted-foreground">
              Create and manage your Kente store
            </p>
          </div>
        </div>
        {onClose && (
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        )}
      </div>

      {/* Store Status */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Package className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Items</p>
                <p className="text-2xl font-bold">{storeStats.totalItems}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Globe className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Published</p>
                <p className="text-2xl font-bold">
                  {storeStats.publishedItems}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Eye className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Views</p>
                <p className="text-2xl font-bold">{storeStats.totalViews}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <TrendingUp className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Sales</p>
                <p className="text-2xl font-bold">{storeStats.totalSales}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="overview" className="flex items-center gap-2">
            <Store className="h-4 w-4" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="items" className="flex items-center gap-2">
            <Package className="h-4 w-4" />
            Items
          </TabsTrigger>
          <TabsTrigger value="customize" className="flex items-center gap-2">
            <Palette className="h-4 w-4" />
            Customize
          </TabsTrigger>
          <TabsTrigger value="preview" className="flex items-center gap-2">
            <Eye className="h-4 w-4" />
            Preview
          </TabsTrigger>
          <TabsTrigger value="publish" className="flex items-center gap-2">
            <Link className="h-4 w-4" />
            Publish
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Store Info */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings className="h-5 w-5" />
                  Store Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-sm font-medium">Store Name</label>
                  <p className="text-lg font-semibold">{storeData.name}</p>
                </div>
                <div>
                  <label className="text-sm font-medium">Description</label>
                  <p className="text-muted-foreground">
                    {storeData.description}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium">Status</label>
                  <div className="mt-1">
                    <Badge
                      variant={storeData.isPublished ? "default" : "secondary"}
                    >
                      {storeData.isPublished ? "Published" : "Draft"}
                    </Badge>
                  </div>
                </div>
                {storeData.storeUrl && (
                  <div>
                    <label className="text-sm font-medium">Store URL</label>
                    <p className="text-sm text-blue-600 break-all">
                      {storeData.storeUrl}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Quick Actions */}
            <Card>
              <CardHeader>
                <CardTitle>Quick Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Button
                  className="w-full justify-start"
                  variant="outline"
                  onClick={() => setActiveTab("items")}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add New Item
                </Button>
                <Button
                  className="w-full justify-start"
                  variant="outline"
                  onClick={() => setActiveTab("customize")}
                >
                  <Palette className="h-4 w-4 mr-2" />
                  Customize Store
                </Button>
                <Button
                  className="w-full justify-start"
                  variant="outline"
                  onClick={() => setActiveTab("preview")}
                >
                  <Eye className="h-4 w-4 mr-2" />
                  Preview Store
                </Button>
                <Button
                  className="w-full justify-start"
                  variant="outline"
                  onClick={() => setActiveTab("publish")}
                >
                  <Link className="h-4 w-4 mr-2" />
                  Publish Store
                </Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="items">
          <StoreManagement
            items={storeData.items}
            onItemsUpdate={(items) => handleStoreUpdate({ items })}
          />
        </TabsContent>

        <TabsContent value="customize" className="mt-6">
          <WebsiteCustomizer
            storeData={storeData}
            onStoreUpdate={handleStoreUpdate}
          />
        </TabsContent>

        <TabsContent value="preview">
          <StorePreview storeData={storeData} />
        </TabsContent>

        <TabsContent value="publish">
          <StoreLinkGenerator
            storeData={storeData}
            onStoreUpdate={handleStoreUpdate}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default StoreBuilder;
